import React, { useState } from 'react';
import { Button, Input, Checkbox, message } from 'antd';
import { PlusOutlined, ClearOutlined, CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import quizClient from '../../client/QuizClient';
import { useSelector } from 'react-redux';
import useTranslation from '../../hooks/useTranslation';

const { TextArea } = Input;

// У каждого варианта свой ключ, выданный при создании: индекс в ключе пересоздавал бы
// поля ввода при любой перестановке, а ключ живёт с вариантом до удаления.
let answerSequence = 0;
const emptyAnswer = () => {
    answerSequence += 1;
    return { key: `answer-${answerSequence}`, text: '', translatedAnswer: '', isCorrect: false };
};
const emptyAnswers = () => [emptyAnswer(), emptyAnswer(), emptyAnswer(), emptyAnswer()];

/**
 * FlagSwitch renders two clickable circles with flags (18px) next to each other,
 * enclosed in a rounded border.
 * For the selected language, it applies the filter 'opacity(90%)',
 * and for the unselected one - 'grayscale(100%) opacity(75%)'.
 *
 * Props:
 * - isTranslationMode: boolean – true if translation mode is active.
 * - onChange: function accepting a new boolean value.
 * - mainLanguage: object { name, flag } for the primary language.
 * - translationLanguage: object { name, flag } for the translation language.
 * - size: size of the circles (default 18px).
 */
const FlagSwitch = ({
                        isTranslationMode,
                        onChange,
                        mainLanguage,
                        translationLanguage,
                        size = 18,
                    }) => {
    const containerStyle = {
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #ccc',
        borderRadius: '12px',
        padding: '4px 6px',
        gap: '8px',
        cursor: 'pointer',
    };

    const circleStyle = (selected, flagUrl) => ({
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundImage: flagUrl ? `url(${flagUrl})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        filter: selected ? 'opacity(90%)' : 'grayscale(100%) opacity(75%)',
        // Кнопка рисует свою рамку и отступы — снимаем, кружок задаётся размером и фоном
        border: 'none',
        padding: 0,
        cursor: 'pointer',
    });

    return (
        <div style={containerStyle}>
            <button
                type="button"
                aria-label={mainLanguage.name}
                aria-pressed={!isTranslationMode}
                onClick={() => onChange(false)}
                style={circleStyle(!isTranslationMode, mainLanguage.flag)}
            />
            <button
                type="button"
                aria-label={translationLanguage.name}
                aria-pressed={isTranslationMode}
                onClick={() => onChange(true)}
                style={circleStyle(isTranslationMode, translationLanguage.flag)}
            />
        </div>
    );
};

const ContentAddQuiz = () => {
    const { t } = useTranslation();

    // Get the selected deck from the Redux store
    const selectedDeck = useSelector((state) => state.deck.selectedDeck);
    // Extract deckId (если в вашей модели колоды поле называется не deckId, замените его на нужное)
    const deckId = selectedDeck ? selectedDeck.deckId : null;

    // Extract main and translation languages
    const mainLanguage = selectedDeck?.locale ?? { name: 'Original', flag: null };
    const translationLanguage = selectedDeck?.translateLocale ?? { name: 'Translate', flag: null };

    // Language mode state: false = Original (default), true = Translation
    const [isTranslationMode, setIsTranslationMode] = useState(false);

    // States for question and translated question
    const [question, setQuestion] = useState('');
    const [translatedQuestion, setTranslatedQuestion] = useState('');

    // States for answers, non-definitive flag, and loading
    const [answers, setAnswers] = useState(emptyAnswers);
    const [answersNotDefinitive, setAnswersNotDefinitive] = useState(false);
    const [loading, setLoading] = useState(false);

    // Handler for changing non-definitive flag for answers
    const handleCheckboxDefinitiveChange = () => {
        setAnswersNotDefinitive(!answersNotDefinitive);
    };

    // Handlers for changing answer texts
    const handleAnswerOriginalChange = (index, value) => {
        const newAnswers = [...answers];
        newAnswers[index].text = value;
        setAnswers(newAnswers);
    };

    const handleAnswerTranslatedChange = (index, value) => {
        const newAnswers = [...answers];
        newAnswers[index].translatedAnswer = value;
        setAnswers(newAnswers);
    };

    // Toggle isCorrect flag for an answer
    const handleCheckboxChange = (index) => {
        const newAnswers = [...answers];
        newAnswers[index].isCorrect = !newAnswers[index].isCorrect;
        setAnswers(newAnswers);
    };

    // Handler for adding a quiz question
    const handleAddQuestion = async () => {
        if (!question.trim()) {
            message.error(t('quiz.questionRequired'));
            return;
        }

        // Check if deckId exists
        if (!deckId) {
            message.error(t('deck.noneSelected'));
            return;
        }

        // Filter answers with non-empty original text
        const filledAnswers = answers.filter(
            (answer) => answer.text.trim() !== ''
        );

        if (filledAnswers.length < 2) {
            message.error(t('quiz.minTwoAnswers'));
            return;
        }

        if (!filledAnswers.some((answer) => answer.isCorrect)) {
            message.error(t('quiz.needCorrectAnswer'));
            return;
        }

        // Transform answers: rename translatedAnswer to translatedText
        const transformedAnswers = filledAnswers.map((answer) => ({
            text: answer.text,
            translatedText: answer.translatedAnswer,
            isCorrect: answer.isCorrect,
        }));

        // Build quiz data including deckId
        const quizData = {
            deckId,
            question,
            translatedQuestion,
            answersNotDefinitive,
            answers: transformedAnswers,
        };

        console.log('Submitting quiz data:', quizData);
        setLoading(true);

        try {
            await quizClient.createQuiz(quizData);
            message.success(t('quiz.added'));
            // Reset form after successful submission
            setQuestion('');
            setTranslatedQuestion('');
            setAnswersNotDefinitive(false);
            setAnswers(emptyAnswers());
        } catch (error) {
            console.error('Error saving the quiz:', error);
            message.error(t('quiz.addFailed', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    // Handler for adding a new answer field
    const handleAddAnswer = () => {
        const newAnswers = [...answers, emptyAnswer()];
        setAnswers(newAnswers);
    };

    // Handler for deleting the last answer field
    const handleDeleteAnswer = () => {
        if (answers.length > 4) {
            const newAnswers = answers.slice(0, -1);
            setAnswers(newAnswers);
        }
    };

    // Handler for clearing the form
    const handleClearForm = () => {
        setQuestion('');
        setTranslatedQuestion('');
        setAnswersNotDefinitive(false);
        setAnswers(emptyAnswers());
    };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
            {/* Text area for question depending on language mode */}
            <TextArea
                rows={4}
                placeholder={
                    isTranslationMode
                        ? t('quiz.translatedQuestionPlaceholder')
                        : t('quiz.questionPlaceholder')
                }
                value={isTranslationMode ? translatedQuestion : question}
                onChange={(e) =>
                    isTranslationMode
                        ? setTranslatedQuestion(e.target.value)
                        : setQuestion(e.target.value)
                }
            />

            {/* Fields for entering answers */}
            {answers.map((answer, index) => (
                <div
                    key={answer.key}
                    style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            width: '100%',
                        }}
                    >
                        <Checkbox
                            checked={answer.isCorrect}
                            onChange={() => handleCheckboxChange(index)}
                        />
                        <TextArea
                            rows={2}
                            placeholder={
                                isTranslationMode
                                    ? t('quiz.answerTranslatedPlaceholder', { number: index + 1 })
                                    : t('quiz.answerPlaceholder', { number: index + 1 })
                            }
                            value={
                                isTranslationMode ? answer.translatedAnswer : answer.text
                            }
                            onChange={(e) =>
                                isTranslationMode
                                    ? handleAnswerTranslatedChange(index, e.target.value)
                                    : handleAnswerOriginalChange(index, e.target.value)
                            }
                        />
                    </div>
                    {index === answers.length - 1 && (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                            }}
                        >
                            <Button
                                type="text"
                                size="small"
                                onClick={handleAddAnswer}
                                style={{ marginLeft: '26px' }}
                                icon={<PlusOutlined />}
                            >
                                {t('quiz.addAnswer')}
                            </Button>
                            {answers.length > 4 && (
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={handleDeleteAnswer}
                                >
                                    {t('common.delete')}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            ))}

            <Checkbox
                checked={answersNotDefinitive}
                onChange={handleCheckboxDefinitiveChange}
            >
                {t('quiz.answersNotDefinitive')}
            </Checkbox>

            {/* Bottom control panel: FlagSwitch on the left and buttons on the right */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                }}
            >
                <div style={{ marginLeft: '12px' }}>
                    <FlagSwitch
                        isTranslationMode={isTranslationMode}
                        onChange={setIsTranslationMode}
                        mainLanguage={mainLanguage}
                        translationLanguage={translationLanguage}
                        size={18}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <Button icon={<ClearOutlined />} onClick={handleClearForm}>
                        {t('common.clear')}
                    </Button>
                    <Button
                        type="primary"
                        icon={<CheckOutlined />}
                        onClick={handleAddQuestion}
                        loading={loading}
                    >
                        {t('quiz.submit')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ContentAddQuiz;
